/**
 * Condition evaluator — the SINGLE isomorphic implementation of a
 * {actualValue, operator, expectedValue} predicate over the 14 `ConditionOperator`
 * values, shared by the backend (V2 parameter rules + configuration-picker
 * discriminators) and the frontend (rule-builder live preview / `testSingleRule`).
 *
 * Previously the backend (`imports/domain/services/condition-evaluator.ts`) and
 * the frontend (`import/utils/RuleEvaluator.ts`) each carried a ~120-line copy
 * that had to be kept "verbatim in sync" by hand — a divergence would make a
 * rule match in the editor preview but not on the real import (or vice-versa).
 *
 * `expectedValue` accepts `string | string[]`: the admin discriminator schema
 * stores `value: string` (comma-separated for `in_list`), while some callers
 * pass a pre-split array. Both are handled.
 *
 * Unknown operators return `false` (the picker treats a non-evaluable
 * discriminator as "did not match" rather than throwing).
 */
import type { ConditionOperator } from '../contracts/imports/custom-importer.contracts.js'
import { parseCsvAmount } from './csv-amount.js'

/**
 * Evaluate a single condition.
 * @param actualValue   The string value read from the CSV row at `column`.
 * @param operator      The comparator.
 * @param expectedValue Either a single string (equals/contains/…) or an array
 *                      of strings (in_list).
 * @param amountFormat  OPTIONAL. When the compared column holds an AMOUNT and
 *                      the caller knows the CSV's locale (`'US'` = `1,234.56`,
 *                      `'EU'` = `1.234,56`), pass it so the numeric comparators
 *                      (`less`/`greater`/`less_equal`/`greater_equal`/`between`)
 *                      parse `actualValue` locale-aware via `parseCsvAmount`
 *                      instead of bare `parseFloat` — which truncates at the
 *                      first separator (`parseFloat('1,234.56')` = 1) so a rule
 *                      like `amount greater 1000` silently never fires (wrong
 *                      classification → wrong tax). When OMITTED, numeric
 *                      parsing is exactly the historical `parseFloat`, so
 *                      existing 3-arg callers are byte-for-byte unchanged.
 */
export function evaluateCondition(
  actualValue: string,
  operator: ConditionOperator,
  expectedValue: string | string[],
  amountFormat?: string,
): boolean {
  const actual = (actualValue ?? '').toLowerCase().trim()

  if (operator === 'in_list') {
    // Discriminator schema declares `value: string`, so the conventional
    // encoding is comma-separated: "Sell,Sold,Sale". Split (or accept an array),
    // normalize, and match the actual value against any entry.
    const list = Array.isArray(expectedValue) ? expectedValue : String(expectedValue).split(',')
    const normalizedList = list.map((v) => v.toLowerCase().trim()).filter((v) => v.length > 0)
    return normalizedList.includes(actual)
  }

  if (operator === 'regex') {
    // Compile/match against the RAW (non-lowercased) values so the author can
    // use uppercase character classes (e.g. `[A-Z]`) and case-sensitive patterns.
    const rawExpected = Array.isArray(expectedValue) ? (expectedValue[0] ?? '') : expectedValue
    try {
      return new RegExp(rawExpected).test(actualValue ?? '')
    } catch {
      // Invalid regex syntax — never throw mid-processing; treat as non-match.
      return false
    }
  }

  // All other operators take a scalar; if an array slipped through, use its
  // first element so `.toLowerCase()` never crashes.
  const expected = Array.isArray(expectedValue)
    ? (expectedValue[0] ?? '').toLowerCase().trim()
    : expectedValue.toLowerCase().trim()

  // Numeric operand parsing for the comparators below.
  //  - `actual` is a CSV amount CELL: when `amountFormat` is provided, parse it
  //    locale-aware via `parseCsvAmount` so EU `1.234,56` and US `1,234.56`
  //    both yield 1234.56 instead of being truncated by bare `parseFloat`.
  //  - `expected` is the rule THRESHOLD, always stored as a canonical
  //    dot-decimal plain number (seeds: `value: '0'`, `'1000'`; the `between`
  //    operator reserves ',' as its min/max delimiter, so a threshold can never
  //    be EU comma-decimal) — so it is parsed as `'US'` (dot-decimal,
  //    unambiguous), NEVER `amountFormat`.
  // When `amountFormat` is undefined, both fall back to the historical
  // `parseFloat` (identical behavior for callers that don't thread the format).
  const parseActualNumber = (raw: string): number =>
    amountFormat === undefined ? parseFloat(raw) : (parseCsvAmount(raw, amountFormat) ?? NaN)
  const parseThresholdNumber = (raw: string): number =>
    amountFormat === undefined ? parseFloat(raw) : (parseCsvAmount(raw, 'US') ?? NaN)

  switch (operator) {
    case 'equals':
      return actual === expected
    case 'not_equals':
      return actual !== expected
    case 'contains':
      return actual.includes(expected)
    case 'starts_with':
      return actual.startsWith(expected)
    case 'ends_with':
      return actual.endsWith(expected)
    // Numeric comparators — if EITHER side is NaN the operator is a non-match.
    case 'less': {
      const numActual = parseActualNumber(actual)
      const numExpected = parseThresholdNumber(expected)
      return !isNaN(numActual) && !isNaN(numExpected) && numActual < numExpected
    }
    case 'greater': {
      const numActual = parseActualNumber(actual)
      const numExpected = parseThresholdNumber(expected)
      return !isNaN(numActual) && !isNaN(numExpected) && numActual > numExpected
    }
    case 'less_equal': {
      const numActual = parseActualNumber(actual)
      const numExpected = parseThresholdNumber(expected)
      return !isNaN(numActual) && !isNaN(numExpected) && numActual <= numExpected
    }
    case 'greater_equal': {
      const numActual = parseActualNumber(actual)
      const numExpected = parseThresholdNumber(expected)
      return !isNaN(numActual) && !isNaN(numExpected) && numActual >= numExpected
    }
    // Unary operators — `expected` is ignored; `actual` is already trimmed so
    // whitespace-only (and null/undefined) count as empty.
    case 'is_empty':
      return actual.length === 0
    case 'is_not_empty':
      return actual.length > 0
    case 'between': {
      // "min,max" — both inclusive. Any malformed range returns false. The ','
      // is the STRUCTURAL min/max delimiter, so min/max are canonical
      // dot-decimal thresholds (parsed as `'US'`); only `actual` (the CSV cell)
      // is locale-aware.
      const parts = expected.split(',')
      if (parts.length !== 2) return false
      const min = parseThresholdNumber((parts[0] ?? '').trim())
      const max = parseThresholdNumber((parts[1] ?? '').trim())
      const numActual = parseActualNumber(actual)
      if (isNaN(min) || isNaN(max) || isNaN(numActual)) return false
      return numActual >= min && numActual <= max
    }
    default:
      return false
  }
}

/**
 * A single rule condition: a `{column, operator, value}` predicate over one row.
 * Structurally shared by the frontend `RuleCondition`, the backend
 * `V2ParameterRule.condition[s]`, and the shared `RuleCondition` contract.
 */
export interface RuleConditionLike {
  column: string
  operator: ConditionOperator
  value: string
}

export type RuleLogicalOperator = 'AND' | 'OR'

/**
 * A rule that may carry EITHER a single legacy `condition` OR a compound
 * `conditions` list combined by `logicalOperator` (issue #266).
 */
export interface CompoundRuleLike {
  condition?: RuleConditionLike | null
  conditions?: RuleConditionLike[] | null
  logicalOperator?: RuleLogicalOperator | null
}

/**
 * Evaluate a parameter rule's predicate against one row — the SINGLE isomorphic
 * combination of compound conditions, shared by the frontend live preview
 * (`testSingleRule`) and the backend import engine (`ruleConditionsMatch`) so
 * the editor preview and the real import NEVER diverge.
 *
 * Semantics (issue #266): a `conditions` list with 2+ entries engages compound
 * mode and is combined with `logicalOperator` ('OR' = at least one matches,
 * otherwise AND = all must match). A 0- or 1-entry list falls back to the legacy
 * single `condition` (a lone `conditions[0]` is used only when no legacy
 * `condition` exists, e.g. a seed-authored compound rule). The caller supplies
 * `evaluateOne` so each side keeps its own column-resolution context.
 */
export function evaluateRulePredicate(
  rule: CompoundRuleLike,
  evaluateOne: (condition: RuleConditionLike) => boolean,
): boolean {
  const compound = Array.isArray(rule.conditions) ? rule.conditions : []
  if (compound.length < 2) {
    const single = rule.condition ?? compound[0]
    return single ? evaluateOne(single) : false
  }
  return (rule.logicalOperator ?? 'AND') === 'OR'
    ? compound.some(evaluateOne)
    : compound.every(evaluateOne)
}
